import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const SourceMediaPanel = ({
  media = [],
  selectedItems = [],
  onSelectItems,
  onDragStart,
  onDragEnd,
  connections = [],
  onPreview
}) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectItems?.(media?.map(item => item?.id));
    } else {
      onSelectItems?.([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectItems?.(prev => [...prev, id]);
    } else {
      onSelectItems?.(prev => prev?.filter(itemId => itemId !== id));
    }
  };

  const getMediaIcon = (type) => {
    switch (type) {
      case 'video': return 'Video';
      case 'image': return 'Image';
      case 'document': return 'FileText';
      case 'audio': return 'Volume2';
      default: return 'File';
    }
  };

  const getStatusBadge = (item) => {
    const connectionCount = connections?.filter(conn => conn?.sourceId === item?.id)?.length || 0;
    const hasConflict = connections?.some(conn => 
      conn?.sourceId === item?.id && conn?.status === 'conflict'
    );

    if (hasConflict) {
      return { label: 'Conflito', color: 'bg-error text-error-foreground', icon: 'AlertCircle' };
    } else if (connectionCount > 0) {
      return { label: `${connectionCount} Conexão${connectionCount > 1 ? 'ões' : ''}`, color: 'bg-success text-success-foreground', icon: 'CheckCircle' };
    } else {
      return { label: 'Disponível', color: 'bg-muted text-muted-foreground', icon: 'Circle' };
    }
  };

  const handleDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    e?.dataTransfer?.setData('text/plain', item?.id);
    onDragStart?.(item);
  };

  const handleDragEnd = () => {
    onDragEnd?.();
  };

  const MediaGridItem = ({ item }) => {
    const status = getStatusBadge(item);
    const isSelected = selectedItems?.includes(item?.id);

    return (
      <div
        className={`bg-card border rounded-lg p-4 shadow-tier-1 transition-all duration-200 cursor-grab active:cursor-grabbing hover:shadow-tier-2 ${
          isSelected ? 'ring-2 ring-primary' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, item)}
        onDragEnd={handleDragEnd}
        data-testid={`source-media-${item?.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
            className="mt-1"
          />
          <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${status?.color}`}>
            <Icon name={status?.icon} size={12} />
            <span>{status?.label}</span>
          </div>
        </div>

        {/* Media Thumbnail */}
        <div className="aspect-video bg-muted rounded-lg mb-3 overflow-hidden">
          {item?.thumbnail ? (
            <img
              src={item?.thumbnail}
              alt={item?.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name={getMediaIcon(item?.type)} size={48} color="var(--color-muted-foreground)" />
            </div>
          )}
        </div>

        {/* Media Info */}
        <div className="space-y-2">
          <h4 className="font-medium text-foreground text-sm line-clamp-2" title={item?.title}>
            {item?.title}
          </h4>
          
          <div className="flex items-center space-x-2 text-xs text-muted-foreground">
            <Icon name={getMediaIcon(item?.type)} size={14} />
            <span className="capitalize">{item?.type}</span>
            <span>•</span>
            <span>{item?.size || 'N/A'}</span>
          </div>

          {/* Additional metadata */}
          {(item?.duration || item?.dimensions || item?.pages) && (
            <div className="text-xs text-muted-foreground">
              {item?.duration && <span>{item?.duration}</span>}
              {item?.dimensions && <span>{item?.dimensions}</span>}
              {item?.pages && <span>{item?.pages} páginas</span>}
            </div>
          )}

          {/* Usage stats */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Usado {item?.usageCount || 0}x
            </span>
            <Button
              variant="ghost"
              size="xs"
              iconName="Eye"
              onClick={() => onPreview?.(item)}
              className="h-6 px-2"
            >
              Preview
            </Button>
          </div>

          {/* Tags */}
          {item?.tags && item?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {item?.tags?.slice(0, 3)?.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
              {item?.tags?.length > 3 && (
                <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-full">
                  +{item?.tags?.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const MediaListItem = ({ item }) => {
    const status = getStatusBadge(item);
    const isSelected = selectedItems?.includes(item?.id);

    return (
      <div
        className={`bg-card border rounded-lg p-4 shadow-tier-1 transition-all duration-200 cursor-grab active:cursor-grabbing hover:shadow-tier-2 ${
          isSelected ? 'ring-2 ring-primary' : ''
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, item)}
        onDragEnd={handleDragEnd}
        data-testid={`source-media-list-${item?.id}`}
      >
        <div className="flex items-center space-x-4">
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
          />

          {/* Thumbnail */}
          <div className="w-16 h-12 bg-muted rounded overflow-hidden flex-shrink-0">
            {item?.thumbnail ? (
              <img
                src={item?.thumbnail}
                alt={item?.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name={getMediaIcon(item?.type)} size={20} color="var(--color-muted-foreground)" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground text-sm truncate" title={item?.title}>
                  {item?.title}
                </h4>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground mt-1">
                  <Icon name={getMediaIcon(item?.type)} size={12} />
                  <span className="capitalize">{item?.type}</span>
                  <span>•</span>
                  <span>{item?.size || 'N/A'}</span>
                  {item?.duration && <><span>•</span><span>{item?.duration}</span></>}
                  {item?.dimensions && <><span>•</span><span>{item?.dimensions}</span></>}
                  {item?.pages && <><span>•</span><span>{item?.pages} páginas</span></>}
                </div>
              </div>

              <div className="flex items-center space-x-2 flex-shrink-0 ml-4">
                <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${status?.color}`}>
                  <Icon name={status?.icon} size={10} />
                  <span>{status?.label}</span>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  iconName="Eye"
                  onClick={() => onPreview?.(item)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-tier-1">
      {/* Panel Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Archive" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Biblioteca de Mídia</h2>
              <p className="text-sm text-muted-foreground font-caption">
                Arraste os itens para mapear ao conteúdo do curso
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              iconName="Grid3X3"
              onClick={() => setViewMode('grid')}
            />
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              iconName="List"
              onClick={() => setViewMode('list')}
            />
          </div>
        </div>

        {/* Select All */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Checkbox
              checked={selectedItems?.length === media?.length && media?.length > 0}
              indeterminate={selectedItems?.length > 0 && selectedItems?.length < media?.length}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
            />
            <span className="text-sm font-medium text-foreground">
              {selectedItems?.length > 0 ? `${selectedItems?.length} selecionado(s)` : 'Selecionar todos'}
            </span>
          </div>

          <p className="text-sm text-muted-foreground font-caption">
            {media?.length} item(s) de mídia
          </p>
        </div>
      </div>

      {/* Media List */}
      <div className="p-6">
        {media?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-4 bg-muted/50 rounded-full flex items-center justify-center">
              <Icon name="Archive" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Nenhuma mídia encontrada
            </h3>
            <p className="text-muted-foreground font-caption">
              Ajuste os filtros ou adicione novos itens à biblioteca
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {media?.map((item) => (
              <MediaGridItem key={item?.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {media?.map((item) => (
              <MediaListItem key={item?.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceMediaPanel;